import * as RadioGroup from '@radix-ui/react-radio-group'
import ColorJS from 'colorjs.io'
import React, { useMemo, useRef } from 'react'
import { GemColors } from '@opallabs/gem'
import { useTheme } from '@opallabs/hooks'
import { ContextMenu, DropdownMenu, ColorInput } from '@opallabs/lapidary'
import * as styles from './styles'

type Color = string | null
type Colors = Array<Color>

export const DEFAULT_COLORS: Colors = [
  null,
  '#3BB273',
  '#F0D057',
  '#FFAE03',
  '#E15554',
  '#9F7DD1',
  '#4790FF',
]

/**
 * Default highlight colors for text highlighting.
 * These are derived from the backend custom field color seeds in:
 * services/elixir/umbrella/apps/loupe/lib/loupe/internal/seeders/seed_custom_field_colors.ex
 */
export const DEFAULT_HIGHLIGHT_COLORS: Colors = [
  '#E1E4EA',
  '#B8F4CC',
  '#C4ECFD',
  '#FFF7AD',
  '#FFD9B8',
  '#FFC2C2',
  '#FFCCEE',
  '#EECDFE',
]

const useMenuColors = (
  colors: Colors,
  useDefaultColors: boolean,
  useDefaultHighlightColors: boolean
) =>
  useMemo(() => {
    if (useDefaultColors) return [...DEFAULT_COLORS, ...colors]
    if (useDefaultHighlightColors)
      return [...DEFAULT_HIGHLIGHT_COLORS, ...colors]
    return colors
  }, [useDefaultColors, useDefaultHighlightColors, colors])

const useSelectedColor = (unsafeColor: Color | undefined) =>
  useMemo<Color>(() => {
    if (!unsafeColor) {
      return null
    }

    const parsedColor = ColorJS.try(unsafeColor)
    return parsedColor
      ? parsedColor.toString({ format: 'hex', collapse: false }).toUpperCase()
      : null
  }, [unsafeColor])

export type ColorSelectionMenuProps = {
  colors?: Colors
  hideColorInput?: boolean
  menuType: 'context' | 'dropdown'
  /** Color definition when the color value is `null` */
  nullColor?: string
  onInputColor?: (color: Color) => void
  onSelectColor: (color: Color) => void
  selectedColor?: Color
  useDefaultColors?: boolean
  useDefaultHighlightColors?: boolean
}

export const ColorSelectionMenu: React.FC<ColorSelectionMenuProps> = ({
  colors = [],
  hideColorInput,
  menuType,
  nullColor = GemColors.N000,
  onInputColor,
  onSelectColor,
  selectedColor: unsafeSelectedColor,
  useDefaultColors = false,
  useDefaultHighlightColors = false,
}) => {
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const BaseMenu = menuType === 'context' ? ContextMenu : DropdownMenu
  const selectedColor = useSelectedColor(unsafeSelectedColor)
  const menuColors = useMenuColors(
    colors,
    useDefaultColors,
    useDefaultHighlightColors
  )

  const handleInputColor = (color: string) => {
    if (menuColors.includes(color)) return onSelectColor(color)

    onInputColor?.(color)
  }

  return (
    <BaseMenu.RadioGroup
      className="color-radio-group"
      css={styles.radioGroup}
      name="Select color"
      useChildren={true}
      value={selectedColor ?? 'none'}
    >
      {menuColors.map((color) => (
        <BaseMenu.RadioItem
          key={color}
          css={styles.radioItem({ theme, selected: selectedColor === color })}
          className="color-radio-item"
          onSelect={() => color !== selectedColor && onSelectColor(color)}
          useChildren={true}
          value={color ?? 'none'}
        >
          <div css={styles.colorSwatch({ color: color ?? nullColor })} />
          <BaseMenu.ItemIndicator
            className="color-radio-indicator"
            css={styles.indicator(theme)}
          />
        </BaseMenu.RadioItem>
      ))}
      {!hideColorInput && (
        <BaseMenu.RadioItem
          css={styles.radioItem({ theme, selected: false })}
          className="color-input"
          onSelect={() => inputRef.current?.click()}
          useChildren={true}
          value="input"
        >
          <ColorInput externalRef={inputRef} onInputColor={handleInputColor} />
        </BaseMenu.RadioItem>
      )}
    </BaseMenu.RadioGroup>
  )
}

export const ColorRadioGroup: React.FC<
  Omit<ColorSelectionMenuProps, 'menuType'>
> = ({
  colors = [],
  hideColorInput,
  nullColor = GemColors.N000,
  onInputColor,
  onSelectColor,
  selectedColor: unsafeSelectedColor,
  useDefaultColors = false,
  useDefaultHighlightColors = false,
}) => {
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const selectedColor = useSelectedColor(unsafeSelectedColor)
  const menuColors = useMenuColors(
    colors,
    useDefaultColors,
    useDefaultHighlightColors
  )

  const handleInputColor = (color: string) => {
    if (menuColors.includes(color)) return onSelectColor(color)

    onInputColor?.(color)
  }

  const handleValueChange = (value: string) => {
    if (value === 'input') return
    // Radix RadioGroup requires string values, so we use 'none' to represent null
    onSelectColor(value === 'none' ? null : value)
  }

  return (
    <RadioGroup.Root
      className="color-radio-group"
      css={styles.radioGroup}
      name="Select color"
      onValueChange={handleValueChange}
      value={selectedColor ?? 'none'}
    >
      {menuColors.map((color) => (
        <RadioGroup.Item
          key={color}
          css={styles.radioItem({ theme, selected: selectedColor === color })}
          className="color-radio-item"
          value={color ?? 'none'}
          onClick={() => {
            if (color === selectedColor) {
              onSelectColor(null)
            }
          }}
          onKeyDown={(e) => {
            if (
              (e.key === 'Enter' || e.key === ' ') &&
              color === selectedColor
            ) {
              onSelectColor(null)
            }
          }}
        >
          <div css={styles.colorSwatch({ color: color ?? nullColor })} />
          <RadioGroup.Indicator
            className="color-radio-indicator"
            css={styles.indicator(theme)}
          />
        </RadioGroup.Item>
      ))}

      {selectedColor && !menuColors.includes(selectedColor) && (
        <RadioGroup.Item
          key={selectedColor}
          css={styles.radioItem({ theme, selected: true })}
          className="color-radio-item"
          value={selectedColor}
          onClick={() => onSelectColor(null)}
        >
          <div css={styles.colorSwatch({ color: selectedColor })} />
          <RadioGroup.Indicator
            className="color-radio-indicator"
            css={styles.indicator(theme)}
          />
        </RadioGroup.Item>
      )}

      {!hideColorInput && (
        <RadioGroup.Item
          css={styles.radioItem({ theme, selected: false })}
          className="color-input"
          onClick={() => inputRef.current?.click()}
          value="input"
        >
          <ColorInput
            externalRef={inputRef}
            onInputColor={handleInputColor}
            value={selectedColor ?? undefined}
          />
        </RadioGroup.Item>
      )}
    </RadioGroup.Root>
  )
}
